import React, { useState, useEffect, SetStateAction } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Select,
  Card,
  Button,
  Tooltip,
  Table,
  message,
  Spin,
  Modal,
  Tag,
  Skeleton,
  Badge,
} from "antd";
import {
  LockFilled,
  UnlockFilled,
  LinkOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  SaveOutlined,
  LockTwoTone,
  LoadingOutlined,
  InfoCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { HubSpotData } from "../interfaces/interfaces";
import { getDealsLastSyncInfo, getLatestLineItems, lockPricingDeal, refreshDeals, UnlockODEPricingDeal, UnlockPricingDeal, updateHubspotLineItems } from "../../services/Apis";
import LineItemCompareTable from "./HubSpotLineItem";
import dayjs from "dayjs";

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { areDatesEqual } from "../services/services";
import { currencySymbolsMap, useCurrency } from "../../context/CurrencyContext";

const { TextArea } = Input;

dayjs.extend(utc);
dayjs.extend(timezone);


const { Option } = Select;

interface LineItemDataType {
  key: string;
  service: string;
  core: number;
  discount: number;
  net: number;
}
interface LineItem {
  line_item_id?: number | null;
  name: string;
  amount: number;
}

interface HubSpotProps {
  disableForm?: boolean;
  hubSpotData: HubSpotData | null;
  pricingContributors: any;
  pricingVersion: any;
  pricingCollaborators: any;
  onChange: (data: any[]) => void;
  versionName: string;
  onUpdatedVersionName: (data: any[]) => void;
  readOnly?: boolean;
  isDealLocked?: boolean;
  setDealLock: any;
  enabledFeatureList: string[];
  setSaveNewPricingVersion: (value: boolean) => void;
  setlockOde: (value: boolean) => void;
  setSendEmail: (value: boolean) => void;
  lineItemData: LineItemDataType[];
  dealId: number;
  distinctServiceLines: string[];
  setReloadNavigation: (value: boolean) => void;
  viewOnly?: boolean;
  basePricingVersionId?: number | null;
  lastRefreshedOn?: any;
  handleODELockAsSave?: () => void;
}

const HubSpot: React.FC<HubSpotProps> = ({
  disableForm = true,
  hubSpotData,
  pricingContributors,
  pricingVersion,
  pricingCollaborators,
  onChange,
  versionName,
  onUpdatedVersionName,
  readOnly = false,
  isDealLocked = false,
  setDealLock = () => { },
  enabledFeatureList,
  setSaveNewPricingVersion,
  setlockOde,
  setSendEmail,
  lineItemData,
  dealId,
  distinctServiceLines,
  setReloadNavigation,
  viewOnly = false,
  basePricingVersionId = null,
  lastRefreshedOn = null,
  handleODELockAsSave = () => {}
}) => {
  const { dealCurrency, currencySymbol } = useCurrency();
  const [form] = Form.useForm();
  const [isTableVisible, setIsTableVisible] = useState(true);
  const [basePricingVersion, setBasePricingVersion] = useState<number | null>(
    basePricingVersionId
  ); // Using string for selected version
  const [dealContributors, setDealContributors] = useState<string[]>(
    hubSpotData?.pdc ? hubSpotData.pdc : []
  );
  const [pricingVersionProjectStartDate, setPricingVersionProjectStartDate] = useState<string>('')
  const [pricingVersionDuration, setPricingVersionDuration] = useState<string | null>(null)
  const [pricingVersionEngagementType, setPricingVersionEngagementType] = useState<string | null>(null)
  const [dealCollaborators, setDealCollaborators] = useState<string[]>([]);
  const [editVersionName, setEditVersionName] = useState(false);
  const [saveVersionName, setSaveVersionName] = useState(false);
  const [newVersionName, setNewVersionName] = useState(versionName);
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [updatedPricingItems, setUpdatedPricingItems] = useState<LineItem[]>(lineItemData.map(item => ({ name: item.service, amount: item.net })));
  const [error, setError] = useState<string | null>(null);
  const [loader, setLoader] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [dealTotalAmount, setDealTotalAmount] = useState<number>(0);
  const [lastSyncTime, setLastSyncTime] = React.useState<string>('')
  const [modalFormInput, setModalFormInput] = React.useState<string>(hubSpotData?.['Unlock Request Reason'] ?? '')
  const [additionalComments, setAdditionalComments] = React.useState<string>(hubSpotData?.['Unlock Comments'] ?? '')
  const [modalFormError, setModalFormError] = React.useState<boolean>(false)
  const hubspotLink = hubSpotData?.['Hubspot Link']
  const [refreshingDeal, setRefreshingDeal] = useState<boolean>(false);
  const [unlockDealModalOpen, setUnlockDealModalOpen] = useState<boolean>(false)

  const boldTextArray = ["Duration","Expected Start Date","Engagement Type"]

  const handleDealRefresh = async () => {
    setRefreshingDeal(true)
    await refreshDeals(dealId).then((resp) => {
      if(resp.error ?? '') {
        message.error(resp.error)
        return
      }
      message.success("Successfully Refreshed the Deal");
      window.location.reload();
    })
    .catch((error) => {
      setRefreshingDeal(false);
      message.error("Failed to Refresh the Deal");
    });
  }

  useEffect(() => {
    const fetchHomeScreenDetails = async () => {
      const lastSyncInfo = await getDealsLastSyncInfo();
      if (lastSyncInfo.error) {
        console.error('Error Response Data:', lastSyncInfo.error);
        console.error('Error Response Status:', lastSyncInfo.error_status);
      } else {
        setLastSyncTime(dayjs(lastSyncInfo['last_refreshed_on']).tz('America/New_York').format('ddd, MMM D, YYYY, h:mm:ss A z'))
      }
    }
    fetchHomeScreenDetails();
  }, [])

  useEffect(() => {
    setPricingVersionProjectStartDate(hubSpotData?.["Pricing Version Project Start Date"] ? hubSpotData["Pricing Version Project Start Date"] : '')
    setPricingVersionDuration(hubSpotData?.["Pricing Version Duration"] ? hubSpotData["Pricing Version Duration"] : null)
    setPricingVersionEngagementType(hubSpotData?.["Pricing Version Engagement Type"] ? hubSpotData["Pricing Version Engagement Type"] : null)
  }, [hubSpotData])
  useEffect(() => {
    setUpdatedPricingItems(lineItemData.map(item => ({ name: item.service, amount: item.net })));
  }, [lineItemData]);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getLatestLineItems(dealId);
      if (result.error_status === 401) {
        window.location.reload();
      } else {
        if (result.error) {
          console.error('Error Response Data:', result.error);
          console.error('Error Response Status:', result.error_status);
          setError(result.error);
          setLoader(false);
        } else {
          setDealTotalAmount(result["deal_amount"]);
          setLineItems(JSON.parse(JSON.stringify(result["hubspot_line_items"])));
          const mergeArrays = (lineItemList: any, pricingAppList: any) => {
            // Create a copy of pricingAppList with an added 'line_item_id: null' property for each object
            const mergedArray = pricingAppList.map((item: any) => ({ ...item, line_item_id: null }));

            lineItemList.forEach((item1: any) => {
              // Check if the name from lineItemList exists in the merged array
              const existingItemIndex = mergedArray.findIndex((item2: any) => item2.name === item1.name);

              if (existingItemIndex !== -1) {
                // If name exists, update the line_item_id in the existing object
                mergedArray[existingItemIndex] = { ...mergedArray[existingItemIndex], line_item_id: item1.line_item_id };
              } else {
                // If name does not exist, add the new object from lineItemList
                if (distinctServiceLines.includes(item1.name))
                  item1.amount = 0 // Set the amount to 0 for new items
                mergedArray.push(item1);
              }
            });

            return mergedArray;
          };
          const newUpdatedPricingItems = mergeArrays(result["hubspot_line_items"], updatedPricingItems);
          setUpdatedPricingItems(newUpdatedPricingItems);
          setError(null);
          setLoader(false);
        }
      }
    }

    if (openModal && !isDealLocked) {
      setLoader(true);
      fetchData();
    };
  }, [openModal]);

  useEffect(() => {
    const selectedPricingContributers = (pricingContributors || []).filter(
      (contributer: any) => dealContributors.includes(contributer.email)
    );
    onChange([
      {
        base_price_version_id: basePricingVersion,
        pricing_contributors: selectedPricingContributers,
      },
    ]);
  }, [basePricingVersion, dealContributors]);

  useEffect(() => {
    if (saveVersionName) {
      onUpdatedVersionName([
        { versionName: versionName, newVersionName: newVersionName },
      ]);
    }
  }, [saveVersionName]);

  // Set the base pricing version to the last item and deal contributors on prop changes
  useEffect(() => {
    // if (pricingVersion && pricingVersion.length > 0) {
    //   // Initialize with the last item from pricing versions
    //   setBasePricingVersion(pricingVersion[pricingVersion.length - 1]?.id);
    // }
    // if (pricingContributors) {
    //   setDealContributors(pricingContributors);
    // }
    if (pricingCollaborators) {
      setDealCollaborators(pricingCollaborators);
    }
  }, [pricingContributors, pricingVersion, pricingCollaborators]);
  if (!hubSpotData) {
    return <p>Loading...</p>;
  }
  const toggleTableVisibility = () => {
    setIsTableVisible(!isTableVisible);
  };

  const handleAmountChange = (name: string, amount: number) => {
    setUpdatedPricingItems((prevState: any) => {
      const serviceLineExists = prevState.findIndex((item: any) => item.name === name);
      if (serviceLineExists !== -1) {
        const updatedServiceLineItems = [...prevState];
        updatedServiceLineItems[serviceLineExists].amount = amount;
        return updatedServiceLineItems;
      } else {
        return [...prevState, { name, amount }];
      }
    });
  }

  const handleODELock = async () => {
    setLoading(true);
    if (isDealLocked && !error) {
      let lockFn = UnlockPricingDeal(parseInt(hubSpotData["HubSpot ID"]), modalFormInput, additionalComments)
      await lockFn
        .then((resp) => {
          setLoading(false);
          if(resp.error ?? '') {
            message.error(resp.error)
            return
          }
          setDealLock(!isDealLocked);
          message.success("Success");
          window.location.reload();
        })
        .catch((error) => {
          setLoading(false);
          message.error("Error Locking/Unlocking the Deal");
        });
    } else {
      if (!error) {
        let updateLineItems = updateHubspotLineItems(dealId, updatedPricingItems, hubSpotData['Version Id'], versionName);
        await updateLineItems.then(async () => {
          setLoading(false);
          setDealLock(!isDealLocked);
          setSendEmail(true);
          setlockOde(true);
          setSaveNewPricingVersion(true);
          setReloadNavigation(true);
          message.success("Success");
        }).catch((error) => {
          setLoading(false);
          message.error("Error Locking/Unlocking the Deal");
        });
      }
    }
    setOpenModal(false);
  };


  const handleODEUnlock = async () => {
    setLoading(true);

    await UnlockODEPricingDeal(parseInt(hubSpotData["HubSpot ID"]), modalFormInput, versionName, additionalComments)
      .then((resp) => {
        setLoading(false);
        if(resp.error ?? '') {
          message.error(resp.error)
          return
        }
        message.success("Successfully sent the unlock request");
      })
      .catch((error) => {
        setLoading(false);
        message.error("Error sending unlock request");
      });

    setUnlockDealModalOpen(false);
  };

  const isAdmin = enabledFeatureList.includes("FE_ADMIN")
  const canUnlockClosedDeals = enabledFeatureList.includes("UNLOCK_CLOSED_DEALS")
  const isDealClosed = (hubSpotData["Deal Stage"].includes("Closed") || hubSpotData["Work Ahead"] === "Yes")
  const handleODEStatus = () => {
    if(canUnlockClosedDeals && isDealLocked && modalFormInput == '' && isDealClosed) {
      message.error("Please enter reason to unlock")
      setModalFormError(true)
      return
    }
    if (!error) {
      handleODELock()
    } else {
      setOpenModal(false)
    }
  }
  const stageListLessThan4 = ["0- New" , "Needs Expressed", "0- New", "Qualified Opportunity", "Connected to Meet", "Discovery", "1- Connected to Meet", "2- Needs Expressed", "3- Qualified Opportunity"]
  // const disableBasePriceVersion = stageListLessThan4.includes(hubSpotData["Deal Stage"]) || hubSpotData["Deal Stage"].includes("Closed") || !enabledFeatureList.includes("FE_EDIT_BASE_PRICE_VERSION") || viewOnly || disableForm
  const disableBasePriceVersion = isDealLocked || !enabledFeatureList.includes("FE_EDIT_BASE_PRICE_VERSION") || viewOnly || disableForm

  return (
    <>
      {!readOnly && (
        <Card
          style={{ marginBottom: "16px" }}
          title="Version Details"
          extra={
            <>
              {isDealLocked && (hubSpotData["Locked Version Id"] === hubSpotData["Version Id"] ? <Tag color="#cd201f">{`ODE Locked on : ${dayjs(hubSpotData["Deal Locked On"]).tz('America/New_York').format('MMM D, YYYY, h:mm:ss A z')}`}</Tag> : <span>{`ODE Locked on : ${dayjs(hubSpotData["Deal Locked On"]).tz('America/New_York').format('MMM D, YYYY, h:mm:ss A z')}`}</span>)}

              {isDealLocked && <Button
                type="text"
                title={isDealLocked ? "Unlock the ODE" : "Lock the ODE"}
                onClick={() => {
                  if(!isDealLocked && lineItemData.length < 1) {
                    message.warning('Please fill Resources before locking')
                    return
                  }
                  if (
                    !canUnlockClosedDeals && isDealLocked && isDealClosed
                  ) {
                    setUnlockDealModalOpen(true)
                    return;
                  }
                  if(!isDealLocked) {
                    handleODELockAsSave()
                    return
                  }
                  setOpenModal(true);
                }}
                disabled={
                  viewOnly ? true : enabledFeatureList.includes("FE_LOCK_ODE")
                    ? disableForm
                    : true
                }
              >
                {loading ? (
                  <Spin size="small" />
                ) : isDealLocked ? (
                    <div style={{ textAlign: 'center', backgroundColor: '#4096ff', paddingTop: '4px', borderRadius: '5px', width: '40px' }}>
                        <LockFilled  style={{ fontSize: '18px', color:"#ffffff" }} />
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', backgroundColor: '#4096ff', paddingTop: '4px', borderRadius: '5px', width: '40px' }}>
                        <UnlockFilled style={{ fontSize: '18px', color:"#ffffff" }} />
                    </div>
                )}
              </Button>
              }
            </>
          }
        >
          <Row align={"middle"}>
            <Col span={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "8px" }}>Version Name:</label>
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 1 }}
                  style={{ width: "60%", whiteSpace: "pre-wrap" }}
                  className={!editVersionName ? "disabled-input" : ""}
                  value={newVersionName}
                  onChange={(e) => setNewVersionName(e.target.value)}
                  disabled={!editVersionName}
                />
                <Button
                  type="text"
                  title={
                    isDealLocked ? "Unlock deal to edit" : "Edit Version Name"
                  }
                  disabled={
                    viewOnly ? true : enabledFeatureList.includes("FE_EDIT_PRICING")
                      ? isDealLocked
                        ? true
                        : false
                      : true
                  }
                  onClick={() => {
                    if (editVersionName) {
                      setSaveVersionName(true);
                    } else {
                      setSaveVersionName(false);
                    }
                    setEditVersionName(!editVersionName);
                  }}
                >
                  {editVersionName ? <SaveOutlined /> : <EditOutlined />}
                </Button>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "8px" }}>
                  Pricing Contributors:
                </label>
                <Tooltip title="Users who are added as Pricing Contributors can view and edit all pricing versions for the deal. They cannot submit pricing for ODE locking.">
                  <Select
                    mode="multiple"
                    placeholder="Add Pricing Contributors"
                    allowClear
                    style={{ width: "60%", marginBottom: "10px" }}
                    disabled={
                      viewOnly ? true : enabledFeatureList.includes("FE_ADD_PRICING_CONTRIBUTORS")
                        ? readOnly
                        : true
                    }
                    value={dealContributors}
                    defaultValue={dealContributors}
                    onChange={(value) => setDealContributors(value)}
                  >
                    {pricingContributors.length > 0 ? (
                      pricingContributors.map((option: any, index: number) => (
                        <Option key={index} value={option.email}>
                          {option.name} - {option.email}
                        </Option>
                      ))
                    ) : (
                      <Option value="No Contributors">No Contributors</Option>
                    )}
                  </Select>
                </Tooltip>
              </div>
            </Col>
          </Row>
          <Row align={"middle"}>
            <Col span={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "8px" }}>Resource Request Status:</label>
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 1 }}
                  style={{ width: "60%", whiteSpace: "pre-wrap" }}
                  className="disabled-input"
                  value={hubSpotData["Resource Request Sent On"] ? `Requested On: ${dayjs(hubSpotData["Resource Request Sent On"]).tz('America/New_York').format('MMM D, YYYY, h:mm:ss A z')}` : 'Resource Request Not Sent'}
                  disabled
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "8px" }}>Version Last Updated HubSpot:</label>
                {hubSpotData["HS Last Updated Version Name"] ? (
                  <a
                    href={`/edit-pricing-details?dealId=${dealId}&versionName=${encodeURIComponent(hubSpotData["HS Last Updated Version Name"])}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {hubSpotData["HS Last Updated Version Name"]}
                  </a>
                ) : (
                  <TextArea
                    autoSize={{ minRows: 1, maxRows: 1 }}
                    style={{ width: "60%", whiteSpace: "pre-wrap" }}
                    className="disabled-input"
                    value="No updated version yet"
                    disabled
                  />
                )}
              </div>
            </Col>

          </Row>
          <Row align={"middle"}>
            <Col span={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "8px" }}>Base Price Versions:</label>
                {/* show drop down values from pricing versions names sort by created on */}
                <Select
                  style={{ width: "60%" }}
                  value={basePricingVersion}
                  onChange={(value) => setBasePricingVersion(value)}
                  disabled={disableBasePriceVersion}
                >
                  {pricingVersion.map((version: any) => (
                    <Option key={version.id} value={version.id}>
                      {version.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <label style={{ marginRight: "8px" }}>ODE Locked Version:</label>
                {hubSpotData["Locked Version Name"] ? (
                  <a
                    href={`/edit-pricing-details?dealId=${dealId}&versionName=${encodeURIComponent(hubSpotData["Locked Version Name"])}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {hubSpotData["Locked Version Name"]}
                  </a>
                ) : (
                  <TextArea
                    autoSize={{ minRows: 1, maxRows: 1 }}
                    style={{ width: "60%", whiteSpace: "pre-wrap" }}
                    className="disabled-input"
                    value="Not Locked"
                    disabled
                  />
                )}
              </div>
            </Col>
            </Row>


        </Card>
      )}
      <Card title="Deal Details from HubSpot" className="hubspot-container"
        extra={
          <div>
            <Tag color="green"> Deal last refreshed on {lastRefreshedOn ?? lastSyncTime} <Tooltip title={"HubSpot data auto refreshes every 30 minutes between 5am - 10pm EST Mon-Fri"}><InfoCircleOutlined /></Tooltip></Tag>
            <Tag color='geekblue' style={{cursor: 'pointer'}} onClick={handleDealRefresh}> Click to Manually Refresh Now <SyncOutlined spin={refreshingDeal}/></Tag>
          </div>
        }
        style={{ marginBottom: "16px"}}>
        {refreshingDeal ? <Skeleton active paragraph={{
          rows: 6,
        }}/> : <Form
          form={form}
          initialValues={hubSpotData}
          labelCol={{ span: 12 }}
          wrapperCol={{ span: 12 }}
        // style={{ maxWidth: 1000 }}
        >
          {Object.entries(hubSpotData)
            .reduce((rows, [key, value], index) => {
              const rowIndex = Math.floor(index / 6);
              if (!rows[rowIndex]) rows[rowIndex] = [];
              if (
                key !== "bpv" &&
                (key !== "pdc" || readOnly) &&
                key !== "Version Id" &&
                key !== "Locked Version Id" &&
                key !== "Pricing Version Project Start Date" &&
                key !== "Pricing Version Duration" &&
                key !== "Pricing Version Engagement Type" &&
                key !== "Deal Amount in HubSpot" &&
                key !== "Deal Line Items" && key !== "Hubspot Link" && key !== "Resource Request Sent On" && key !== "Locked Version Name" && key !== "Deal Locked On" && key !== "HS Last Updated Version Name" &&
                key !== "Solution Lead Name" && key !== "Delivery Lead Name"
                && key !== 'Unlock Request Reason' && key !== 'Unlock Comments'
              )
                rows[rowIndex].push(
                  <Col span={key == "Deal Name" ? 16 : 8} key={key}>
                    <Form.Item
                      style={{ marginBottom: "8px", fontWeight: boldTextArray?.includes(key) ? 'bold': '' }}
                      label={key !== "pdc" ? key : "Pricing Contributors"}
                      validateStatus={
                        (key == 'Duration' && value.replace(' months', '') <= 0) ? "error" :
                          key == 'Duration' && value.replace(' months', '') != pricingVersionDuration ? "error" :
                            key == 'Expected Start Date' && !areDatesEqual(value, pricingVersionProjectStartDate) ? "error" :
                              key == 'Engagement Type' && value != pricingVersionEngagementType ? "error" : ""}
                      help={
                        (key == 'Duration' && value.replace(' months', '') <= 0) ? "Duration is not valid!" :
                          key == 'Duration' && value.replace(' months', '') != pricingVersionDuration ? `Old Duration: ${pricingVersionDuration}` :
                            key == 'Expected Start Date' && !areDatesEqual(value, pricingVersionProjectStartDate) ? `Old Expected Start Date: ${pricingVersionProjectStartDate}` :
                              key == 'Engagement Type' && value != pricingVersionEngagementType ? `Old Engagement Type: ${pricingVersionEngagementType}` : ""}
                      name={key}
                      labelCol={key == "Deal Name" ? { span: 6 } : { span: 12 }} // Custom label column span
                      wrapperCol={key == "Deal Name" ? { span: 18 } : { span: 12 }} // Custom input column span
                    >
                      {/* <Input className="disabled-input" value={value} disabled /> */}
                      {key == "HubSpot ID" ? (
                        <a
                          target="_blank"
                          href={hubspotLink}
                        >
                          {value}
                        </a>
                      ) : (
                        <TextArea
                          autoSize={{ minRows: 1, maxRows: 5 }}
                          style={{ width: "100%", whiteSpace: "pre-wrap", fontWeight: boldTextArray?.includes(key) ? 'bold': '' }}
                          className="disabled-input"
                          value={value}
                          disabled
                        />
                      )}
                    </Form.Item>
                  </Col>
                );
              return rows;
            }, [] as React.ReactNode[][])
            .map((cols, i) => (
              <Row key={i}>{cols}</Row>
            ))}

          <Row style={{ marginBottom: "16px" }}>
            <Col span={8}>
              <Form.Item
                label="Deal Amount in HubSpot"
                name="Deal Amount in HubSpot"
                labelCol={{ span: 12 }} // Custom label column span
                wrapperCol={{ span: 12 }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Input
                    className="disabled-input"
                    value={`${currencySymbol} ${new Intl.NumberFormat().format(
                      hubSpotData["Deal Amount in HubSpot"]
                    )}`}
                    disabled
                  />
                  <Button
                    type="text"
                    icon={isTableVisible ? <MinusOutlined /> : <PlusOutlined />}
                    onClick={toggleTableVisibility}
                  />
                </div>

                {isTableVisible && (
                  <Table
                    dataSource={hubSpotData["Deal Line Items"]}
                    className="custom-table"
                    columns={[
                      {
                        title: "Name",
                        dataIndex: "name",
                        key: "name",
                        render: (text: string) => !distinctServiceLines.includes(text) ? <Tag color="lightgray">{text}</Tag> : text,
                      },
                      {
                        title: "Amount",
                        dataIndex: "amount",
                        key: "amount",
                        render: (text:number, record:any) =>
                          !distinctServiceLines.includes(record.name) ? <Tag color="lightgray">{`${currencySymbolsMap[record.currency]} ${new Intl.NumberFormat().format(text)}`}</Tag> :
                          `${currencySymbolsMap[record.currency]} ${new Intl.NumberFormat().format(text)}`,
                      },
                    ]}
                    pagination={false}
                    size="small"
                    components={{
                      header: {
                        wrapper: (
                          props: React.HTMLAttributes<HTMLTableSectionElement>
                        ) => <thead style={{ display: "none" }} {...props} />,
                      },
                    }}
                  />
                )}
              </Form.Item>
            </Col>
            <Col
              span={8}
            >
              <Form.Item
                style={{ marginBottom: "8px", fontWeight: '' }}
                label="Solution Lead"
                name="Solution Lead Name"
                labelCol={{ span: 12 }} // Custom label column span
                wrapperCol={{ span: 12 }}
              >
                <Input
                  className="disabled-input"
                  value={hubSpotData["Solution Lead Name"]}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col
              span={8}            >
              <Form.Item
                style={{ marginBottom: "8px", fontWeight: '' }}
                label="Delivery Lead"
                name="Delivery Lead Name"
                labelCol={{ span: 12 }} // Custom label column span
                wrapperCol={{ span: 12 }}
              >
                <Input
                  className="disabled-input"
                  value={hubSpotData["Delivery Lead Name"]}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        }
        <Badge status="default" text="Engagement Type, Expected Start Date, and Duration directly impact resource allocation plan and pricing calculations. Ensure you review and adjust the resource allocation plan accordingly after making any updates to these fields in HubSpot." />
        <Modal
          title={isDealLocked ? "Unlock the Deal?" : "Lock Pricing Version as ODE?"}
          open={openModal}
          onCancel={() => setOpenModal(false)}
          onOk={handleODEStatus}
          okText={"Confirm"}
          okButtonProps={{ loading: loading }}
          width={'70%'}
        >
          <span className="text-center w-100" style={{ display: loader ? 'block' : 'none' }}><Spin /> Loading Line Items from HubSpot </span>
          {error && <p className='text-danger'>Error: {error}</p>}
          {!loader && !error &&
            <>
              <p>
                <span style={{ fontWeight: "bold" }}>Deal : </span>
                {hubSpotData["Deal Name"]}
              </p>
              <p>
                <span style={{ fontWeight: "bold" }}>Version : </span>
                {versionName}
              </p>
              {(canUnlockClosedDeals && isDealLocked && isDealClosed) &&
                <>
                  <p>
                    <span style={{ fontWeight: "bold" }}>Stage : </span>
                    {hubSpotData["Deal Stage"]}
                  </p>
                  <p>
                    <span style={{ fontWeight: "bold" }}>Reason to Unlock <Tooltip title={`Required - Reason to Admin Override`}><InfoCircleOutlined /></Tooltip> </span>
                    <TextArea value={modalFormInput} onChange={(e)=>setModalFormInput(e.target.value)} status={modalFormError ? 'error': ''}/>
                  </p>
                  <p>
                    <span style={{ fontWeight: "bold" }}>Additional Comments </span>
                    <TextArea value={additionalComments} onChange={(e)=>setAdditionalComments(e.target.value)}/>
                  </p>
                </>
              }
              {/* <br /> */}
              {!isDealLocked &&
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <div className="mb-2" style={{display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '3px', marginLeft: '4px'}}>
                    <strong>By confirming, the following actions will occur:</strong>
                    <li><strong>&emsp;► Resource request email notification:</strong> A notification will be sent with the ODE status included.</li>
                    <li><strong>&emsp;► HubSpot Line Item Updates:</strong> The following line items will be automatically updated in HubSpot</li>
                  </div>
                  <LineItemCompareTable dataSrc={lineItemData} totalDealAmount={dealTotalAmount} distinctServiceLines={distinctServiceLines} hubspotLineItems={lineItems as LineItem[]} updatedPricingItems={updatedPricingItems} handleAmountChange={handleAmountChange} />
                  <p style={{marginTop: '8px'}}><strong>Note:</strong> Once deal is locked with ODE, <strong>you will NOT be able to edit any pricing version.</strong> To make changes after locking, you must unlock ODE by clicking the "ODE Unlock" button at the top-right corner of the Pricing page. If the deal stage is in <strong>Stage 7</strong> or contains <strong>WAC</strong>, unlocking will require an admin request.. Otherwise, you can unlock and resubmit pricing as ODE.</p>
                </div>
              }
            </>
          }
        </Modal>
        <Modal
          title={"Unlock the Deal?"}
          open={unlockDealModalOpen}
          onCancel={() => setUnlockDealModalOpen(false)}
          onOk={handleODEUnlock}
          okText={"Send Email Request"}
          okButtonProps={{ loading: loading, disabled: modalFormInput.length < 1 }}
        >
          We are unable to unlock this deal at this stage.
          <br/> 
          Please send an email to the admin to request unlocking.
          <div style={{marginTop: '8px'}}>
            <span style={{ fontWeight: "bold" }}>Reason to Unlock <Tooltip title={`Required`} ><span style={{color: 'red'}}>*</span></Tooltip> </span>
            <TextArea value={modalFormInput} onChange={(e)=>setModalFormInput(e.target.value)} status={modalFormError ? 'error': ''}/>
          </div>
        </Modal>
      </Card>
    </>
  );
};

export default HubSpot;
